import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import type {
  Character,
  Entity,
  EntityEventChange,
  EntityType,
  EventSummary,
  Location,
  NarrativeSummary,
  Organization,
  Scene,
  SceneSnapshot
} from '@/lib/types';
import { applyDeltas } from '@/lib/utils/apply-deltas';
import { VOICE_ARCHETYPES } from '@common/schemas/entities/character.schema';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

export function useSceneSnapshot() {
  const {
    novel,
    getSummaries,
    entityDao,
    narrativeDao,
  } = useNovelRepository();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [isCreating, setIsCreating] = useState(false);

  const createSnapshot = useCallback(async (scene: Scene) => {
    if (!novel.id) return;

    setIsCreating(true);
    try {
      const novelId = novel.id;
      const sceneId = scene.id;

      // 1. Identify Previous Scene (for AI context)
      const allChapters = getSummaries<NarrativeSummary>('chapters').sort((a, b) => (a.order || 0) - (b.order || 0));
      const allScenes = getSummaries<NarrativeSummary>('scenes');
      const currentChapterId = scene.chapterId;
      const currentChapterIndex = allChapters.findIndex(c => c.id === currentChapterId);
      const sameChapterScenes = allScenes
        .filter(s => s.chapterId === currentChapterId)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      const currentSceneIndex = sameChapterScenes.findIndex(s => s.id === sceneId);

      let previousSceneId: string | null = null;
      let previousSceneContentId: string | null = null;

      if (currentSceneIndex > 0) {
        previousSceneId = sameChapterScenes[currentSceneIndex - 1].id;
      } else if (currentChapterIndex > 0) {
        const prevChapter = allChapters[currentChapterIndex - 1];
        const lastChapterScenes = allScenes
          .filter(s => s.chapterId === prevChapter.id)
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        if (lastChapterScenes.length > 0) {
          previousSceneId = lastChapterScenes[lastChapterScenes.length - 1].id;
        }
      }

      if (previousSceneId) {
        const prevScene = await narrativeDao?.getItem<Scene>(novel.ownerId, novel, 'narrative', 'scenes', previousSceneId);
        if (prevScene) {
          previousSceneContentId = prevScene.mainContentId || null;
        }
      }

      // 2. Identify Anchor Event
      const allEvents = getSummaries<EventSummary>('events').sort((a, b) => (a.order || 0) - (b.order || 0));
      let anchorEventId: string | null = null;
      let anchorEventOrder = -1;

      if (scene.eventIds && scene.eventIds.length > 0) {
        const firstEventId = scene.eventIds[0];
        const firstEvent = allEvents.find(e => e.id === firstEventId);
        if (firstEvent) {
          // Assume the initial state is the event exactly 1 order prior
          anchorEventOrder = (firstEvent.order || 0) - 1;
        }
      } else {
        // Find nearest previous scene with events
        let foundAnchor = false;

        // Check same chapter first
        for (let i = currentSceneIndex - 1; i >= 0; i--) {
          const prevSceneSummary = sameChapterScenes[i];
          const prevSceneData = await narrativeDao?.getItem<Scene>(novel.ownerId, novel, 'narrative', 'scenes', prevSceneSummary.id);

          if (prevSceneData?.eventIds && prevSceneData.eventIds.length > 0) {
            anchorEventId = prevSceneData.eventIds[prevSceneData.eventIds.length - 1];
            const eventSummary = allEvents.find(e => e.id === anchorEventId);
            anchorEventOrder = eventSummary?.order ?? 0;
            foundAnchor = true;
            break;
          }
        }

        // If not found in same chapter, check previous chapters
        if (!foundAnchor) {
          for (let i = currentChapterIndex - 1; i >= 0; i--) {
            const prevChapter = allChapters[i];
            const chapterScenes = allScenes
              .filter(s => s.chapterId === prevChapter.id)
              .sort((a, b) => (b.order || 0) - (a.order || 0)); // Descending to find last scene with events first

            for (const sSummary of chapterScenes) {
              const prevSceneData = await narrativeDao?.getItem<Scene>(novel.ownerId, novel, 'narrative', 'scenes', sSummary.id);

              if (prevSceneData?.eventIds && prevSceneData.eventIds.length > 0) {
                anchorEventId = prevSceneData.eventIds[prevSceneData.eventIds.length - 1];
                const eventSummary = allEvents.find(e => e.id === anchorEventId);
                anchorEventOrder = eventSummary?.order ?? 0;
                foundAnchor = true;
                break;
              }
            }
            if (foundAnchor) break;
          }
        }
      }

      // 2. Reconstruct Entity State
      const reconstructedEntities = {
        characters: {} as Record<string, Character>,
        locations: {} as Record<string, Location>,
        organizations: {} as Record<string, Organization>
      };

      const relevantEntityMap: Record<Exclude<EntityType, 'events' | 'codex'>, string[]> = {
        characters: scene.characterIds || [],
        locations: scene.locationIds || [],
        organizations: scene.organizationIds || []
      };

      for (const [type, ids] of Object.entries(relevantEntityMap)) {
        const entityType = type as Exclude<EntityType, 'events'>;
        // Build label map for this entity type
        const typeSettings = novel.settings?.[entityType] || [];
        const fieldLabels: Record<string, string> = {};
        typeSettings.forEach((section) => {
          section.fields?.forEach((field) => {
            fieldLabels[field.id] = field.label;
          });
        });

        for (const id of ids) {
          const baseData = await entityDao?.getItem<Entity>(novel.ownerId, novel, 'entities', type as EntityType, id);
          if (!baseData) continue;

          // 1. Literal deconstruction and reconstruction for the schema
          const {
            id: entityId, name, shortDescription, description, images,
            primaryImageId, mainImageUrl, mainImagePath, visualDescription, details: originalDetails,
            audit, isArchived
          } = baseData;

          const reconstructed: Record<string, unknown> = {
            id: entityId,
            name,
            shortDescription: shortDescription || '',
            description: description || '',
            images: images || [],
            primaryImageId: primaryImageId ?? null,
            mainImageUrl: mainImageUrl || '',
            mainImagePath: mainImagePath || '',
            visualDescription: visualDescription || '',
            details: originalDetails || {},
            audit,
            isArchived: isArchived || false
          };

          if (type === 'characters') {
            const char = baseData as Character;
            reconstructed.type = char.type;
            reconstructed.fullName = char.fullName || '';
            reconstructed.nickname = char.nickname || '';
            reconstructed.characterClass = char.characterClass || '';
            reconstructed.organizationIds = char.organizationIds || [];

            // Fallback: Create a voice profile on the spot if missing
            if (!char.voiceProfile) {
              const randomIndex = Math.floor(Math.random() * VOICE_ARCHETYPES.length);
              const randomArchetype = VOICE_ARCHETYPES[randomIndex];
              const generatedProfile = {
                archetype: randomArchetype.name,
                baseStyle: { ...randomArchetype.baseStyle },
                verbalTics: []
              };

              // Update the character in Firestore to keep it consistent
              if (entityDao) {
                await entityDao.updateEntity(novel.ownerId, novel, 'characters', id, { voiceProfile: generatedProfile });
              }

              // Use it in the snapshot
              reconstructed.voiceProfile = generatedProfile;
            } else {
              reconstructed.voiceProfile = char.voiceProfile;
            }
          } else if (type === 'locations') {
            const loc = baseData as Location;
            reconstructed.parentId = loc.parentId || null;
            reconstructed.parentName = loc.parentName || null;
            reconstructed.type = loc.type || '';
            reconstructed.region = loc.region || '';
          } else if (type === 'organizations') {
            const org = baseData as Organization;
            reconstructed.type = org.type || '';
          }

          // 2. Time-travel reconstruction (Apply chronological event changes)
          if (baseData.eventChanges && baseData.eventChanges.length > 0) {
            const changes = baseData.eventChanges as EntityEventChange[];
            const relevantChanges = changes
              .map(ec => {
                const eventSummary = allEvents.find(e => e.id === ec.eventId);
                return { ...ec, _resolvedOrder: eventSummary?.order ?? -1 };
              })
              .filter(ec => ec._resolvedOrder >= 0 && ec._resolvedOrder <= anchorEventOrder)
              .sort((a, b) => a._resolvedOrder - b._resolvedOrder);

            for (const ec of relevantChanges) {
              Object.assign(reconstructed, applyDeltas(reconstructed, ec.changes));
            }
          }

          // 3. Replace IDs with labels in details and clean invalid values
          const newDetails: Record<string, string> = {};
          for (const fieldId of Object.keys(fieldLabels)) {
            const label = fieldLabels[fieldId];
            let value = reconstructed[fieldId];
            if (value === undefined && reconstructed.details) {
              value = (reconstructed.details as Record<string, unknown>)[fieldId];
            }

            if (label && value !== null && value !== undefined && String(value).trim() !== '') {
              newDetails[label] = String(value);
            }
          }
          reconstructed.details = newDetails;

          if (type === 'characters') reconstructedEntities.characters[id] = reconstructed as Character;
          else if (type === 'locations') reconstructedEntities.locations[id] = reconstructed as Location;
          else if (type === 'organizations') reconstructedEntities.organizations[id] = reconstructed as Organization;
        }
      }

      // 3. Persist Snapshot
      const now = new Date().toISOString();

      // Resolve POV character name from snapshot entities
      const povChar = scene.povCharacterId
        ? Object.values(reconstructedEntities.characters).find(c => c.id === scene.povCharacterId)
        : undefined;

      const snapshot: SceneSnapshot = {
        id: 'current',
        novelId,
        chapterId: scene.chapterId || '',
        sceneId,
        canonRevision: novel.canonRevision || '1',
        data: {
          sceneGoal: scene.goal || '',
          tone: novel.tone || '',
          constraints: scene.constraints || [],
          language: novel.language ?? 'English',
          sceneDescription: scene.description ?? null,
          sceneConflict: scene.conflict ?? null,
          sceneThemes: scene.themes ?? null,
          scenePointOfScene: scene.pointOfScene ?? null,
          sceneMinorCharacters: scene.minorCharacters ?? null,
          sceneSettings: scene.settings ?? null,
          sceneTitle: scene.name ?? null,
          scenePovCharacterName: povChar?.name ?? null,
          entities: {
            characters: Object.values(reconstructedEntities.characters),
            locations: Object.values(reconstructedEntities.locations),
            organizations: Object.values(reconstructedEntities.organizations),
            loreRules: []
          },
          glossary: novel.glossary || {}
        },
        anchorEventId,
        previousSceneId,
        previousSceneContentId,
        createdAt: now
      };

      if (!narrativeDao) return;

      await narrativeDao.saveSceneSnapshot(novel.ownerId, novelId, sceneId, snapshot);

      // Update the scene to mark that a snapshot has been created
      await narrativeDao.updateScenePack(novel.ownerId, novel, sceneId, { isSnapshotCreated: true });

      toast({
        title: t('entity_view.scenes.snapshot_success'),
        variant: 'default',
      });
    } catch (error) {
      console.error('Failed to create snapshot:', error);
      toast({
        title: t('entity_view.scenes.snapshot_failed'),
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  }, [novel, entityDao, narrativeDao, getSummaries, t, toast]);

  return {
    createSnapshot,
    isCreating
  };
}
